import { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { DailySummaryGenerator } from './DailySummaryGenerator';
import { WeeklyReportGenerator } from './WeeklyReportGenerator';
import { StrategicReportGenerator } from './StrategicReportGenerator';
import { IntelligenceReportGenerator } from './IntelligenceReportGenerator';
import { ManualNotificationCreator } from './ManualNotificationCreator';
import { ReportGenerationStatus } from './ReportGenerationStatus';
import { 
  Calendar, 
  FileText, 
  TrendingUp, 
  Brain, 
  Bell, 
  Building2,
  Activity
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useOrganizationInitialization } from '@/hooks/useOrganizationInitialization';
import { useAvailableBusinesses } from '@/lib/api-extensions/admin-queries';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

type ViewType = 'daily' | 'weekly' | 'strategic' | 'intelligence' | 'manual';

interface SidebarItemProps {
  active: boolean;
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}

function SidebarItem({ active, icon, label, onClick }: SidebarItemProps) {
  return (
    <Button
      variant={active ? "secondary" : "ghost"}
      className={cn(
        "w-full justify-start gap-3 mb-1",
        active ? "bg-slate-100 text-slate-900 font-medium" : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
      )}
      onClick={onClick}
    >
      {icon}
      {label}
    </Button>
  );
}

export function AdminNotificationsDashboard() {
  const [currentView, setCurrentView] = useState<ViewType>('daily');
  const { organizationData } = useOrganizationInitialization();
  const { data: businesses } = useAvailableBusinesses();
  const [selectedBusinessId, setSelectedBusinessId] = useState<number | 'all' | null>(null);

  // Filter businesses by current organization
  const filteredBusinesses = useMemo(() => {
    if (!organizationData || !businesses) return [];
    return businesses.filter(b => b.organization_id === organizationData.id);
  }, [organizationData, businesses]);

  const renderContent = () => {
    const commonProps = {
      organizationId: organizationData?.id || null,
      businessId: selectedBusinessId === 'all' ? null : selectedBusinessId
    };

    switch (currentView) {
      case 'daily':
        return <DailySummaryGenerator {...commonProps} />;
      case 'weekly':
        return <WeeklyReportGenerator {...commonProps} />;
      case 'strategic':
        return <StrategicReportGenerator {...commonProps} />;
      case 'intelligence':
        return <IntelligenceReportGenerator {...commonProps} />;
      case 'manual':
        return <ManualNotificationCreator />;
      default:
        return <DailySummaryGenerator {...commonProps} />;
    }
  };

  const getTitle = () => {
    switch (currentView) {
      case 'daily': return 'Daily Summaries';
      case 'weekly': return 'Weekly Reports';
      case 'strategic': return 'Strategic Reports';
      case 'intelligence': return 'Intelligence Reports';
      case 'manual': return 'Manual Notification';
      default: return 'Dashboard';
    }
  };

  const getDescription = () => {
    switch (currentView) {
      case 'daily': return 'Generate daily summary notifications for users.';
      case 'weekly': return 'Generate weekly performance reports.';
      case 'strategic': return 'Generate comprehensive 6-month strategic analysis.';
      case 'intelligence': return 'Generate AI-powered market intelligence reports.';
      case 'manual': return 'Create and send custom notifications to users.';
      default: return '';
    }
  };

  return (
    <div className="flex flex-col md:flex-row gap-6 min-h-[600px] items-start">
      {/* Left Sidebar: Navigation */}
      <div className="w-full md:w-64 flex-shrink-0 space-y-2">
        <div className="mb-4 px-2">
          <h3 className="font-semibold text-lg tracking-tight">Operations</h3>
          <p className="text-sm text-muted-foreground">Select a tool to manage</p>
        </div>
        
        <div className="space-y-1">

          <SidebarItem 
            active={currentView === 'daily'} 
            icon={<Calendar className="h-4 w-4" />} 
            label="Daily Summaries" 
            onClick={() => setCurrentView('daily')} 
          />
          <SidebarItem 
            active={currentView === 'weekly'} 
            icon={<FileText className="h-4 w-4" />} 
            label="Weekly Reports" 
            onClick={() => setCurrentView('weekly')} 
          />
          <SidebarItem 
            active={currentView === 'strategic'} 
            icon={<TrendingUp className="h-4 w-4" />} 
            label="Strategic Reports" 
            onClick={() => setCurrentView('strategic')} 
          />
          <SidebarItem 
            active={currentView === 'intelligence'} 
            icon={<Brain className="h-4 w-4" />} 
            label="Intelligence Reports" 
            onClick={() => setCurrentView('intelligence')} 
          />
          
          <SidebarItem 
            active={currentView === 'manual'} 
            icon={<Bell className="h-4 w-4" />} 
            label="Manual Notification" 
            onClick={() => setCurrentView('manual')} 
          />
        </div>

        {/* Organization & Business Context */}
        <div className="mt-8 px-2 pt-6 border-t">
          <div className="mb-4">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1.5 block">
              Current Organization
            </label>
            <div className="flex items-center gap-2 text-sm font-medium text-slate-900">
              <Building2 className="h-4 w-4 text-slate-500" />
              {organizationData?.name || 'Loading...'}
            </div>
          </div>

          {currentView !== 'weekly' && (
            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1.5 block flex items-center gap-1">
                Target Business
                <span className="text-red-500">*</span>
              </label>
              <Select 
                value={selectedBusinessId === 'all' ? "all" : selectedBusinessId?.toString()} 
                onValueChange={(val) => setSelectedBusinessId(val === "all" ? 'all' : Number(val))}
              >
                <SelectTrigger className={cn(
                  "w-full h-9 text-sm transition-all duration-200",
                  !selectedBusinessId && "border-red-300 ring-2 ring-red-100 hover:border-red-400"
                )}>
                  <SelectValue placeholder="Select a Business (Required)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Businesses</SelectItem>
                  {filteredBusinesses.map((business) => (
                    <SelectItem key={business.id} value={business.id.toString()}>
                      {business.brand_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      </div>

      {/* Center: Main Workspace */}
      <div className="flex-1 min-w-0 w-full">
        <div className="mb-6">
          <h2 className="text-2xl font-bold tracking-tight">{getTitle()}</h2>
          <p className="text-muted-foreground">{getDescription()}</p>
        </div>
        
        {renderContent()}
      </div>

      {/* Right Sidebar: System Status */}
      <div className="w-full xl:w-80 flex-shrink-0 space-y-4">
        <div className="rounded-xl border bg-white shadow-sm p-4">
          <div className="flex items-center gap-2 mb-4 pb-3 border-b">
            <Activity className="h-4 w-4 text-slate-500" />
            <h3 className="font-semibold text-sm">System Status</h3>
          </div>
          <ReportGenerationStatus compact={true} className="shadow-none border-0 p-0" />
        </div>
      </div>
    </div>
  );
}
